import { useContext } from 'react';
import {
  NavigationRouteContext,
  useNavigation,
  type NavigationProp,
} from '@react-navigation/native';
import { useInstanceId, useNativeEvent } from 'shared/src/hooks';
import { isNil, isNotNil } from 'shared/src/utils';
import { getQueryParam } from 'shared/src/utils/navigator/utils';
import type {
  GoodsDetailExtras,
  GoodsRouteParams,
} from '../../../hooks/useGoodsRoute';
import { parseGoodsSnoFromDeeplink } from '../utils/parseGoodsSnoFromDeeplink';
import { useGoodsSno } from './useGoodsSno';

const safeParseExtras = (value: string): GoodsDetailExtras | undefined => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

// flag 는 top-level 쿼리파라미터 또는 extras JSON 중첩, 두 발행 포맷 모두 확인한다.
const parseSameMarketFlag = (url: string): boolean => {
  const top = getQueryParam(url, 'same_market_recommend_only');
  if (isNotNil(top)) return decodeURIComponent(top) === 'true';

  const extras = getQueryParam(url, 'extras');
  if (isNil(extras)) return false;
  return (
    safeParseExtras(decodeURIComponent(extras))?.same_market_recommend_only ===
    true
  );
};

/**
 * warm 서피스 재사용으로 같은 goods 를 재진입할 때(네이티브가 incomingDeeplink 로 새 딥링크 전달),
 * 이미 마운트된 화면은 옛 params 를 들고 있어 same_market_recommend_only 가 반영되지 않는다.
 * 새 딥링크의 flag 를 현재 화면 params 에 setParams 로 반영해 필터를 재평가한다.
 */
export const useSameMarketReentry = () => {
  const instanceId = useInstanceId();
  const navigation =
    useNavigation<NavigationProp<GoodsRouteParams, 'GoodsDetail'>>();
  const route = useContext(NavigationRouteContext);
  const goodsSno = useGoodsSno();

  useNativeEvent('incomingDeeplink', instanceId, ({ incomingDeeplink }) => {
    if (parseGoodsSnoFromDeeplink(incomingDeeplink) !== goodsSno) return;

    const params = route?.params as GoodsRouteParams['GoodsDetail'] | undefined;
    const currentExtras =
      typeof params?.extras === 'string'
        ? safeParseExtras(params.extras)
        : params?.extras;
    if (isNil(currentExtras)) return;

    navigation.setParams({
      extras: {
        ...currentExtras,
        same_market_recommend_only: parseSameMarketFlag(incomingDeeplink),
      },
    });
  });
};
